import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiRequestError, api } from './api.ts';
import type { MeDto } from './api.ts';

interface AuthState {
  me: MeDto | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  isManager: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const queryClient = useQueryClient();
  const [me, setMe] = useState<MeDto | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setMe(await api.get<MeDto>('/auth/me'));
    } catch (error) {
      // A 401 here is the normal signed-out case, not a failure worth showing.
      if (error instanceof ApiRequestError && error.status === 401) {
        setMe(null);
      } else {
        throw error;
      }
    }
  }, []);

  useEffect(() => {
    // Swallowed here rather than inside `refresh`, which `login` calls and
    // whose errors that path does want to see. On the first load there is
    // nobody to tell: anything other than a session — a 429, a server still
    // starting, a dropped connection — means the same thing to the router as
    // signed out, and rethrowing only produced an unhandled rejection.
    void refresh()
      .catch(() => {
        setMe(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      await api.post('/auth/login', { email, password });
      // Nothing cached before this point belongs to the account signing in.
      queryClient.clear();
      await refresh();
    },
    [refresh, queryClient],
  );

  const logout = useCallback(async () => {
    await api.post('/auth/logout').catch(() => undefined);
    setMe(null);

    // The QueryClient is a module-level singleton that outlives sign-out, and
    // no query key carries a user or workspace identity. Without this, the next
    // person to sign in on the same browser is served the previous agent's
    // conversation queue and profile straight from cache.
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      me,
      loading,
      login,
      logout,
      refresh,
      isManager: me?.role === 'owner' || me?.role === 'admin',
    }),
    [me, loading, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
